import React, { useMemo, useRef } from 'react';
import { ArrowRight } from 'lucide-react';
import type { NavigationTile, NavigationTilesData } from 'types/websiteBuilder';
import { compressImageFile } from 'utils/imageUtils';
import galleryPlaceholder from 'assets/websiteBuilderDefaults/gallery-placeholder.svg';
import styles from './NavigationTilesSection.module.scss';

const TILE_COUNT = 3;
const DEFAULT_TITLES = ['Get To Knows Us', 'Events', 'Next Steps'];
const DEFAULT_DESCRIPTION_PLACEHOLDER = 'Enter description here';
const DEFAULT_TITLE_PLACEHOLDER = 'Enter title here';
const DEFAULT_LINK = '/';
const DEFAULT_BUTTON_TEXT = 'Learn More';

interface NavigationTilesSectionEditorProps {
  data: Record<string, unknown>;
  onUpdateData: (data: Record<string, unknown>) => void;
}

function getDefaultTile(index: number): NavigationTile {
  return {
    imageUrl: galleryPlaceholder,
    title: DEFAULT_TITLES[index] ?? DEFAULT_TITLE_PLACEHOLDER,
    description: '',
    link: DEFAULT_LINK,
    buttonText: DEFAULT_BUTTON_TEXT,
  };
}

function getData(data: Record<string, unknown>): NavigationTilesData {
  const tilesIn = Array.isArray(data.tiles) ? data.tiles : [];

  // New templates should show a useful starting UI.
  if (tilesIn.length === 0) {
    return {
      tiles: Array.from({ length: TILE_COUNT }, (_, i) => getDefaultTile(i)),
    };
  }

  const normalizedTiles = Array.from({ length: TILE_COUNT }, (_, i) => {
    const raw = tilesIn[i] as Record<string, unknown> | undefined;
    if (!raw) {
      return {
        imageUrl: '',
        title: '',
        description: '',
        link: '',
        buttonText: '',
      };
    }

    return {
      imageUrl: typeof raw.imageUrl === 'string' ? raw.imageUrl : '',
      title: typeof raw.title === 'string' ? raw.title : '',
      description: typeof raw.description === 'string' ? raw.description : '',
      link: typeof raw.link === 'string' ? raw.link : '',
      buttonText: typeof raw.buttonText === 'string' ? raw.buttonText : '',
    };
  });

  return { tiles: normalizedTiles };
}

export function NavigationTilesSectionEditor({
  data,
  onUpdateData,
}: NavigationTilesSectionEditorProps) {
  const d = useMemo(() => getData(data), [data]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadIndexRef = useRef<number>(-1);

  const updateTile = (index: number, patch: Partial<NavigationTile>) => {
    const next = [...(d.tiles ?? [])];
    const tile = next[index] ?? {};
    next[index] = { ...tile, ...patch };
    onUpdateData({ ...data, tiles: next });
  };

  const clearTile = (index: number) => {
    updateTile(index, {
      imageUrl: '',
      title: '',
      description: '',
      link: '',
      buttonText: '',
    });
  };

  const addTile = (index: number) => {
    updateTile(index, getDefaultTile(index));
  };

  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file == null) return;

    const index = uploadIndexRef.current;
    // Keep image payload small enough for localStorage persistence.
    compressImageFile(file, { maxWidth: 640, maxHeight: 640, quality: 0.72 })
      .then((imageUrl) => {
        if (index >= 0) updateTile(index, { imageUrl });
      })
      .catch(() => {});

    e.target.value = '';
    uploadIndexRef.current = -1;
  };

  const triggerImageUpload = (index: number) => {
    uploadIndexRef.current = index;
    fileInputRef.current?.click();
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className={styles.fileInput}
        onChange={handleImageFileChange}
        aria-label="Upload tile image"
      />

      <div className={styles.editorGrid}>
        {d.tiles?.map((tile, i) => {
          const isActive = Boolean(tile.imageUrl?.trim());
          return (
            <div key={i} className={styles.tileEditorCard}>
              <div className={styles.tileEditorHeader}>
                <div className={styles.tileEditorTitle}>Tile {i + 1}</div>
                <button
                  type="button"
                  className={[
                    styles.tileEditorActionBtn,
                    !isActive ? '' : styles.tileEditorActionBtnDanger,
                  ].join(' ')}
                  onClick={() => (isActive ? clearTile(i) : addTile(i))}
                  aria-label={
                    isActive ? `Remove tile ${i + 1}` : `Add tile ${i + 1}`
                  }
                >
                  {isActive ? 'Remove' : 'Add'}
                </button>
              </div>

              <div className={styles.tileEditorThumbRow}>
                <input
                  type="text"
                  className={styles.tileEditorInput}
                  value={tile.imageUrl ?? ''}
                  onChange={(e) => updateTile(i, { imageUrl: e.target.value })}
                  placeholder="Image URL"
                  aria-label={`Tile ${i + 1} image URL`}
                />
                <button
                  type="button"
                  className={styles.browseBtn}
                  onClick={() => triggerImageUpload(i)}
                >
                  Browse
                </button>
                <button
                  type="button"
                  className={styles.clearBtn}
                  onClick={() => clearTile(i)}
                  disabled={!isActive}
                >
                  Clear
                </button>
              </div>

              <div className={styles.tileEditorFields}>
                <div className={styles.tileEditorLabelRow}>Title</div>
                <input
                  type="text"
                  className={styles.tileEditorInput}
                  value={tile.title ?? ''}
                  onChange={(e) => updateTile(i, { title: e.target.value })}
                  placeholder={DEFAULT_TITLE_PLACEHOLDER}
                />

                <div className={styles.tileEditorLabelRow}>Description</div>
                <textarea
                  className={styles.tileEditorTextarea}
                  value={tile.description ?? ''}
                  onChange={(e) =>
                    updateTile(i, { description: e.target.value })
                  }
                  placeholder={DEFAULT_DESCRIPTION_PLACEHOLDER}
                  rows={2}
                />

                <div className={styles.tileEditorLabelRow}>Link</div>
                <input
                  type="text"
                  className={styles.tileEditorInput}
                  value={tile.link ?? ''}
                  onChange={(e) => updateTile(i, { link: e.target.value })}
                  placeholder={DEFAULT_LINK}
                />

                <div className={styles.tileEditorLabelRow}>Button Text</div>
                <input
                  type="text"
                  className={styles.tileEditorInput}
                  value={tile.buttonText ?? ''}
                  onChange={(e) =>
                    updateTile(i, { buttonText: e.target.value })
                  }
                  placeholder={DEFAULT_BUTTON_TEXT}
                />
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

interface NavigationTilesSectionProps {
  data: Record<string, unknown>;
  onUpdateData: (data: Record<string, unknown>) => void;
}

function NavigationTilesSection({ data }: NavigationTilesSectionProps) {
  const d = useMemo(() => getData(data), [data]);

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        <div className={styles.tilesGrid}>
          {(d.tiles ?? []).map((tile, i) => {
            const isActive = Boolean(tile.imageUrl?.trim());
            const imageSrc = tile.imageUrl?.trim()
              ? tile.imageUrl
              : galleryPlaceholder;
            const title = tile.title?.trim()
              ? tile.title
              : DEFAULT_TITLE_PLACEHOLDER;
            const desc = tile.description?.trim()
              ? tile.description
              : DEFAULT_DESCRIPTION_PLACEHOLDER;
            const link = tile.link?.trim() ? tile.link : DEFAULT_LINK;
            const buttonText = tile.buttonText?.trim()
              ? tile.buttonText
              : DEFAULT_BUTTON_TEXT;

            return (
              <div
                key={i}
                className={`${styles.tile} ${!isActive ? styles.inactiveTile : ''}`}
              >
                <a
                  href={link}
                  className={styles.tileLink}
                  onClick={(e) => {
                    if (!isActive) e.preventDefault();
                  }}
                  aria-label={title}
                >
                  <img
                    src={imageSrc}
                    alt=""
                    className={`${styles.tileImage} ${!isActive ? styles.tileImageInactive : ''}`}
                  />
                  <div className={styles.tileOverlay} />
                  {isActive && (
                    <div className={styles.tileContent}>
                      <div className={styles.tileTitle}>{title}</div>
                      <div className={styles.tileDesc}>{desc}</div>
                      <div className={styles.tileAction}>
                        <span>{buttonText}</span>
                        <ArrowRight size={14} />
                      </div>
                    </div>
                  )}
                </a>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default NavigationTilesSection;
